import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock3, Copy, Database, ExternalLink, Eye, EyeOff, FileAudio, Folder, ImageOff, Link2, Music, Pencil, Plus, Search, Trash2, Tv, Upload, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useSession } from '../app/session';
import { ask } from '../components/Confirm';
import { useAssetUrls } from '../components/EntityForm';
import { Pager, usePaged } from '../components/Pager';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';
import { Categories, useCategories } from './MediaLibraryPage';

export interface Track {
  id: string;
  title: string;
  artist: string | null;
  album: string | null;
  durationSec: number | null;
  coverAssetId: string | null;
  sourceUrl: string;
  enabled: boolean;
  categories: { categoryId: string }[];
  createdAt?: string;
}
export interface Asset {
  id: string;
  url: string;
  bytes: number;
  kind: string;
  mimeType: string;
  createdAt: string;
}

/** Full body for PUT /aod/tracks/:id (the API replaces the whole track). */
export const trackBody = (t: Track, patch: Partial<Track> = {}) => {
  const x = { ...t, ...patch };
  return {
    title: x.title,
    artist: x.artist,
    album: x.album,
    durationSec: x.durationSec,
    coverAssetId: x.coverAssetId,
    sourceUrl: x.sourceUrl,
    enabled: x.enabled,
    categoryIds: x.categories.map((c) => c.categoryId),
  };
};

export const mmss = (s: number | null) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '—');
export const fileSize = (b: number) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : b >= 1024 ** 2 ? `${(b / 1024 ** 2).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Uploaded file behind a track (when its URL is one of ours). */
export const assetFor = (assets: Asset[], url: string) => {
  try {
    const path = new URL(url, location.origin).pathname;
    return assets.find((a) => a.url === path) ?? null;
  } catch {
    return null;
  }
};

/** Audio format and file name from the URL. */
export function audioInfo(u: string, asset: Asset | null) {
  let file = '—';
  let host = '';
  try {
    const url = new URL(u, location.origin);
    file = decodeURIComponent(url.pathname.split('/').pop() || '') || '—';
    host = url.hostname === location.hostname ? '' : url.hostname.replace(/^www\./, '');
  } catch {
    /* not a URL */
  }
  const ext = (file.split('.').pop() ?? '').toLowerCase();
  const format = asset?.mimeType === 'audio/mpeg' || ext === 'mp3' ? 'MP3' : asset?.mimeType === 'audio/aac' || ext === 'aac' ? 'AAC' : ext === 'm4a' || asset?.mimeType === 'audio/mp4' ? 'AAC (M4A)' : ext ? ext.toUpperCase() : '—';
  return { file, format, source: asset ? 'Uploaded file' : host ? `External (${host})` : 'Server URL' };
}

/** Reads the length of a local audio file in the browser (seconds), or null. */
export function audioSeconds(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const a = document.createElement('audio');
    const src = URL.createObjectURL(file);
    let settled = false;
    const end = (n: number | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(src);
      resolve(n);
    };
    a.preload = 'metadata';
    a.onloadedmetadata = () => end(isFinite(a.duration) && a.duration > 0 ? Math.round(a.duration) : null);
    a.onerror = () => end(null);
    setTimeout(() => end(null), 8000);
    a.src = src;
  });
}

export const AUDIO_ACCEPT = 'audio/mpeg,audio/mp3,audio/aac,audio/mp4,audio/x-m4a,.mp3,.aac,.m4a';
export const MAX_AUDIO = 200 * 1024 * 1024;

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: ReactNode; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

export function AodPage() {
  const [tab, setTab] = useState<'tracks' | 'categories'>('tracks');
  const navigate = useNavigate();
  const { can } = useSession();
  const [uploading, setUploading] = useState(false);
  return (
    <>
      <PageHeader
        title="AOD – Music"
        sub="Music for the rooms (AAC / MP3). Shared by all sites; packages decide who hears what."
        actions={
          can('ADMIN') && (
            <>
              <button className="btn" onClick={() => setUploading(true)}>
                <Upload size={16} /> Upload music
              </button>
              <button className="btn primary" onClick={() => navigate('/content/aod/new')}>
                <Plus size={16} /> Add track
              </button>
            </>
          )
        }
      />
      <Tracks tab={tab} setTab={setTab} uploading={uploading} setUploading={setUploading} />
    </>
  );
}

function Tracks({ tab, setTab, uploading, setUploading }: { tab: 'tracks' | 'categories'; setTab: (t: 'tracks' | 'categories') => void; uploading: boolean; setUploading: (v: boolean) => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const url = useAssetUrls();
  const cats = useCategories('aod');
  const query = useQuery({ queryKey: ['aod', 'items'], queryFn: () => api.get<{ items: Track[] }>('/aod/tracks') });
  const assets = useQuery({ queryKey: ['assets'], queryFn: () => api.get<{ items: Asset[] }>('/assets') });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['aod', 'items'] });
  const put = useMutation({ mutationFn: ({ t, patch }: { t: Track; patch: Partial<Track> }) => api.put(`/aod/tracks/${t.id}`, trackBody(t, patch)), onSuccess: invalidate });
  const remove = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api.delete(`/aod/tracks/${id}`);
    },
    onSuccess: () => {
      setChecked(new Set());
      return invalidate();
    },
  });
  const bulk = useMutation({
    mutationFn: async (patch: Partial<Track>) => {
      for (const t of all.filter((x) => checked.has(x.id))) await api.put(`/aod/tracks/${t.id}`, trackBody(t, patch));
    },
    onSuccess: invalidate,
  });
  const duplicate = useMutation({ mutationFn: (t: Track) => api.post('/aod/tracks', { ...trackBody(t), title: `${t.title} (copy)`.slice(0, 200), enabled: false }), onSuccess: invalidate });
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('title');
  const [selId, setSelId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const all = query.data?.items ?? [];
  const assetList = assets.data?.items ?? [];
  const catList = cats.data?.items ?? [];
  const catName = (id: string) => catList.find((c) => c.id === id)?.name ?? '…';
  const items = all
    .filter((t) => {
      const s = q.trim().toLowerCase();
      if (s && ![t.title, t.artist, t.album].some((x) => x?.toLowerCase().includes(s))) return false;
      if (cat !== 'all' && !t.categories.some((c) => c.categoryId === cat)) return false;
      if (status === 'on' && !t.enabled) return false;
      if (status === 'off' && t.enabled) return false;
      return true;
    })
    .sort((a, b) =>
      sort === 'title-desc'
        ? b.title.localeCompare(a.title)
        : sort === 'artist'
          ? (a.artist ?? '').localeCompare(b.artist ?? '') || a.title.localeCompare(b.title)
          : sort === 'duration'
            ? (b.durationSec ?? 0) - (a.durationSec ?? 0)
            : sort === 'added'
              ? (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
              : a.title.localeCompare(b.title),
    );
  const { rows: paged, pager } = usePaged(items, 12);
  const sel = all.find((t) => t.id === selId) ?? paged[0] ?? null;
  const pageIds = paged.map((t) => t.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => checked.has(id));
  const admin = can('ADMIN');
  const libBytes = all.reduce((n, t) => n + (assetFor(assetList, t.sourceUrl)?.bytes ?? 0), 0);
  const edit = (t: Track) => navigate(`/content/aod/${t.id}`);
  const del = (t: Track) => ask(`Delete ${t.title}? It disappears from the TVs.`, () => remove.mutate([t.id]));

  return (
    <>
      <div className="stat-strip">
        <StatCard icon={<Music size={22} />} tone="violet" value={all.length} label="Total tracks" sub="Music in the library" />
        <StatCard icon={<Folder size={22} />} tone="violet" value={catList.length} label="Categories" sub="Packages unlock by category" />
        <StatCard icon={<Tv size={22} />} tone="green" value={all.filter((t) => t.enabled).length} label="Visible on TV" sub="Available in rooms" />
        <StatCard icon={<Database size={22} />} tone="blue" value={libBytes ? fileSize(libBytes) : '—'} label="Uploaded audio" sub="Files stored on this server" />
      </div>

      <div className="row" style={{ gap: 8, margin: '16px 0 12px' }}>
        <button className={`pill-tab${tab === 'tracks' ? ' on' : ''}`} onClick={() => setTab('tracks')}>
          <Music size={15} style={{ verticalAlign: -2 }} /> Tracks
        </button>
        <button className={`pill-tab${tab === 'categories' ? ' on' : ''}`} onClick={() => setTab('categories')}>
          <Folder size={15} style={{ verticalAlign: -2 }} /> Categories
        </button>
      </div>

      {tab === 'categories' ? (
        <Categories kind="aod" />
      ) : query.isLoading ? (
        <Loading />
      ) : (
        <div className="fac-layout vod-layout">
          <div className="card fill">
            <div className="filters">
              <label className="search">
                <Search size={16} />
                <input placeholder="Search tracks, artist, album…" value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <Select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
                <option value="all">All categories</option>
                {catList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                <option value="all">All statuses</option>
                <option value="on">Visible on TV</option>
                <option value="off">Hidden</option>
              </Select>
              <Select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
                <option value="title">Title A → Z</option>
                <option value="title-desc">Title Z → A</option>
                <option value="artist">Artist</option>
                <option value="duration">Longest first</option>
                <option value="added">Recently added</option>
              </Select>
              <span className="muted" style={{ marginLeft: 'auto', fontSize: 13 }}>
                {items.length} items
              </span>
            </div>
            {checked.size > 0 && admin && (
              <div className="bulk-bar" style={{ marginTop: 12 }}>
                <strong>{checked.size} selected</strong>
                <button className="btn small" disabled={bulk.isPending} onClick={() => bulk.mutate({ enabled: true })}>
                  <Eye size={14} /> Show on TV
                </button>
                <button className="btn small" disabled={bulk.isPending} onClick={() => bulk.mutate({ enabled: false })}>
                  <EyeOff size={14} /> Hide
                </button>
                <button className="btn small danger" onClick={() => ask(`Delete ${checked.size} tracks? They disappear from the TVs.`, () => remove.mutate([...checked]))}>
                  <Trash2 size={14} /> Delete
                </button>
                <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setChecked(new Set())} title="Clear selection">
                  <X size={16} />
                </button>
              </div>
            )}
            {items.length === 0 ? (
              <Empty>{all.length ? 'No track matches.' : 'No music yet. Use “Upload music” to add MP3 or AAC files.'}</Empty>
            ) : (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="fac-table">
                  <thead>
                    <tr>
                      {admin && (
                        <th style={{ width: 34 }}>
                          <input
                            type="checkbox"
                            checked={allOnPage}
                            aria-label="Select this page"
                            onChange={() =>
                              setChecked((c) => {
                                const n = new Set(c);
                                pageIds.forEach((id) => (allOnPage ? n.delete(id) : n.add(id)));
                                return n;
                              })
                            }
                          />
                        </th>
                      )}
                      <th>Title</th>
                      <th>Artist</th>
                      <th>Album / source</th>
                      <th>Category</th>
                      <th>Duration</th>
                      <th>On TV</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((t) => {
                      const img = url(t.coverAssetId);
                      return (
                        <tr key={t.id} className={sel?.id === t.id ? 'selected' : ''} onClick={() => setSelId(t.id)}>
                          {admin && (
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={checked.has(t.id)}
                                aria-label={`Select ${t.title}`}
                                onChange={() =>
                                  setChecked((c) => {
                                    const n = new Set(c);
                                    if (n.has(t.id)) n.delete(t.id);
                                    else n.add(t.id);
                                    return n;
                                  })
                                }
                              />
                            </td>
                          )}
                          <td>
                            <span className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                              <span className="fac-thumb cover">{img ? <img src={img} alt="" /> : <Music size={14} />}</span>
                              <strong>{t.title}</strong>
                            </span>
                          </td>
                          <td className="muted">{t.artist || '—'}</td>
                          <td className="muted" style={{ fontSize: 13, maxWidth: 180 }}>
                            {t.album || '—'}
                          </td>
                          <td>
                            <span className="row" style={{ gap: 4 }}>
                              {t.categories.slice(0, 2).map((c) => (
                                <span key={c.categoryId} className="chip" style={{ color: '#5b21b6', background: '#f1ebff' }}>
                                  {catName(c.categoryId)}
                                </span>
                              ))}
                              {t.categories.length > 2 && <span className="muted">+{t.categories.length - 2}</span>}
                            </span>
                          </td>
                          <td>{mmss(t.durationSec)}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Toggle on={t.enabled} onChange={(v) => admin && put.mutate({ t, patch: { enabled: v } })} label="Visible on TV" />
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                            {admin && (
                              <>
                                <button className="icon-btn" title="Edit" onClick={() => edit(t)}>
                                  <Pencil size={16} />
                                </button>
                                <button className="icon-btn" title="Duplicate (hidden copy)" onClick={() => duplicate.mutate(t)}>
                                  <Copy size={16} />
                                </button>
                                <button className="icon-btn danger-icon" title="Delete" onClick={() => del(t)}>
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Pager {...pager} />
            <ErrorBox error={query.error ?? put.error ?? remove.error ?? bulk.error ?? duplicate.error} />
          </div>

          {sel && (
            <TrackDetail
              t={sel}
              img={url(sel.coverAssetId)}
              asset={assetFor(assetList, sel.sourceUrl)}
              catName={catName}
              admin={admin}
              onToggle={(v) => put.mutate({ t: sel, patch: { enabled: v } })}
              onEdit={() => edit(sel)}
            />
          )}
        </div>
      )}
      {uploading && <BulkUpload categories={catList} onClose={() => setUploading(false)} />}
    </>
  );
}

function TrackDetail({ t, img, asset, catName, admin, onToggle, onEdit }: { t: Track; img: string | null; asset: Asset | null; catName: (id: string) => string; admin: boolean; onToggle: (v: boolean) => void; onEdit: () => void }) {
  const info = audioInfo(t.sourceUrl, asset);
  const row = (icon: ReactNode, label: string, value: ReactNode) => (
    <div className="fac-detail-row">
      {icon}
      <span>{label}</span>
      <span className="fac-detail-val">{value}</span>
    </div>
  );
  return (
    <div className="card fac-detail">
      <div className="track-cover">{img ? <img src={img} alt="" /> : <Music size={40} />}</div>
      <audio className="track-audio" controls preload="none" src={t.sourceUrl} />
      <h2 style={{ margin: '12px 0 0', fontSize: 20 }}>{t.title}</h2>
      <div className="muted">{t.artist || 'Unknown artist'}</div>
      <div className="fac-detail-list">
        {row(<Folder size={16} />, 'Album / source', t.album || '—')}
        {row(<Clock3 size={16} />, 'Duration', mmss(t.durationSec))}
        {row(<FileAudio size={16} />, 'Audio format', info.format)}
        {row(<Database size={16} />, 'File size', asset ? fileSize(asset.bytes) : '—')}
        {row(<Link2 size={16} />, 'Source', info.source)}
        {row(
          <ExternalLink size={16} />,
          'Source URL',
          <a href={t.sourceUrl} target="_blank" rel="noreferrer" className="link-ellipsis" title={t.sourceUrl}>
            {info.file}
          </a>,
        )}
        {row(
          <Folder size={16} />,
          'Categories',
          t.categories.length ? (
            <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
              {t.categories.map((c) => (
                <span key={c.categoryId} className="chip" style={{ color: '#5b21b6', background: '#f1ebff' }}>
                  {catName(c.categoryId)}
                </span>
              ))}
            </span>
          ) : (
            'None'
          ),
        )}
      </div>
      <div className="row" style={{ gap: 12, marginTop: 14, padding: '12px 0', borderTop: '1px solid var(--line)', flexWrap: 'nowrap' }}>
        <Tv size={18} color="var(--muted)" />
        <span style={{ flex: 1 }}>
          <strong style={{ display: 'block', fontSize: 14 }}>Visible on TV</strong>
          <span className="muted" style={{ fontSize: 12.5 }}>
            Available for guests in rooms
          </span>
        </span>
        <Toggle on={t.enabled} onChange={(v) => admin && onToggle(v)} label="Visible on TV" />
      </div>
      {admin && (
        <button className="btn primary" style={{ width: '100%', justifyContent: 'center' }} onClick={onEdit}>
          <Pencil size={16} /> Edit track
        </button>
      )}
    </div>
  );
}

/** Pick several MP3/AAC files: each is uploaded and becomes a track (title from the file name). */
function BulkUpload({ categories, onClose }: { categories: { id: string; name: string }[]; onClose: () => void }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [cat, setCat] = useState('');
  const [rows, setRows] = useState<{ name: string; p: number; state: 'waiting' | 'uploading' | 'done' | 'error'; error?: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const run = async (files: File[]) => {
    setBusy(true);
    setRows(files.map((f) => ({ name: f.name, p: 0, state: 'waiting' })));
    const patch = (i: number, p: Partial<(typeof rows)[number]>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...p } : x)));
    for (let i = 0; i < files.length; i++) {
      const f = files[i]!;
      if (f.size > MAX_AUDIO) {
        patch(i, { state: 'error', error: 'Larger than 200 MB' });
        continue;
      }
      patch(i, { state: 'uploading' });
      try {
        const secs = await audioSeconds(f);
        const up = api.uploadWithProgress<{ url: string }>('/assets/audio', f, (p) => patch(i, { p }));
        const a = await up.done;
        const title = f.name.replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').trim().slice(0, 200) || 'Untitled';
        await api.post('/aod/tracks', { title, sourceUrl: new URL(a.url, location.origin).href, durationSec: secs, enabled: true, categoryIds: cat ? [cat] : [] });
        patch(i, { state: 'done', p: 1 });
      } catch (e) {
        patch(i, { state: 'error', error: e instanceof Error ? e.message : 'Failed' });
      }
    }
    await qc.invalidateQueries({ queryKey: ['aod', 'items'] });
    await qc.invalidateQueries({ queryKey: ['assets'] });
    setBusy(false);
  };
  const finished = rows.length > 0 && !busy;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal medium" role="dialog" aria-label="Upload music">
        <div className="modal-head">
          <h2>Upload music</h2>
          <button className="icon-btn" disabled={busy} onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>
        <div className="modal-body stack">
          {rows.length === 0 ? (
            <>
              <label className="camp-field">
                <span className="camp-label">Category for the new tracks</span>
                <Select value={cat} onChange={(e) => setCat(e.target.value)}>
                  <option value="">No category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </label>
              <button type="button" className="audio-drop" onClick={() => input.current?.click()}>
                <Music size={30} />
                <strong>Choose MP3 or AAC files</strong>
                <span>Several at once. Up to 200 MB each. The file name becomes the title; edit the artist and cover afterwards.</span>
              </button>
            </>
          ) : (
            <div className="upload-list">
              {rows.map((r, i) => (
                <div key={i}>
                  <span className="upload-name">
                    {r.state === 'done' ? <Check size={15} color="#047857" /> : r.state === 'error' ? <X size={15} color="#dc2626" /> : <FileAudio size={15} />} {r.name}
                  </span>
                  {r.state === 'error' ? (
                    <span className="camp-hint err">{r.error}</span>
                  ) : (
                    <span className="upload-bar">
                      <span style={{ width: `${r.p * 100}%` }} />
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <input
            ref={input}
            type="file"
            multiple
            accept={AUDIO_ACCEPT}
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (files.length) void run(files);
            }}
          />
        </div>
        <div className="modal-foot">
          <span className="muted" style={{ marginRight: 'auto', fontSize: 13 }}>
            {rows.length ? `${rows.filter((r) => r.state === 'done').length} of ${rows.length} added` : ''}
          </span>
          <button className="btn primary" disabled={busy} onClick={onClose}>
            {finished ? 'Done' : busy ? 'Uploading…' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
